import { Sentry } from '@wix/editor-error-reporter/dist/getSentry';
import type * as SentryTypes from '@sentry/browser';
import getQueryUtils from '@wix/santa-main-r/lib/lib/common/getQueryUtils';
import { CustomTransport } from './transport';
import type {
  EditorModel,
  BILoggerModel,
} from '@wix/editor-error-reporter/dist/types';

(window as any).Sentry = Sentry; //in case it's blocked by adblocker

const queryUtil = getQueryUtils(window);

const VERSION_REGEXP = /\/(\d+\.\d+\.\d+)\/?$/;

function getBaseVersion(url: string) {
  const semverMatches = url.match(VERSION_REGEXP);
  return semverMatches ? semverMatches[1] : undefined;
}

const buildDefaultTagStore = (
  editorModel: Partial<EditorModel>,
  editorVersion: string | undefined,
  biLoggerModel: Partial<BILoggerModel>,
  appStudioModel: unknown,
) => ({
  numberOfPages: editorModel.siteHeader?.pagesCount,
  revision: editorModel.siteHeader?.revision,
  geo: editorModel.geo,
  editorVersion,
  metaSiteId: editorModel.metaSiteId,
  isDesktopEditor: true,
  isMobileEditor: false,
  esi: editorModel.editorSessionId,
  editorLoaded: false,
  isPreview: false,
  isAppStudio: appStudioModel !== undefined,
  ds: biLoggerModel.origin,
  isNewBundle: Boolean(biLoggerModel.isNewBundle),
});

const buildDefaultContext = (editorModel: Partial<EditorModel>) => ({
  runningExperiments: editorModel.runningExperiments,
  isPublished: editorModel.siteHeader?.published,
});

const isWixCodeError = (event: SentryTypes.Event): boolean => {
  const exceptionFrames =
    event.exception?.values?.[0]?.stacktrace?.frames ?? [];

  return exceptionFrames
    .filter((frame) => frame && typeof frame.filename === 'string')
    .some(
      (frame) =>
        frame.filename!.includes('wixCode') ||
        frame.filename!.includes('wix-code'),
    );
};

const shouldSkipEvent = (message: string = ''): boolean =>
  message.includes(
    "play() failed because the user didn't interact with the document first",
  ) ||
  message.includes('The play() request was interrupted by a call to pause()');

const initSentry = () => {
  const { editorModel, loggerModel, appStudioModel, serviceTopology } =
    window as any as {
      editorModel: EditorModel;
      loggerModel: BILoggerModel;
      appStudioModel: unknown;
      serviceTopology: any;
    };
  //todo remove scriptsLocationMap usage when new server merged
  const editorBase =
    editorModel.editorBase ??
    (serviceTopology.scriptsLocationMap['santa-editor'] || '');
  const editorVersion = getBaseVersion(editorBase);

  const enabled =
    queryUtil.isParameterTrue('force-enable-sentry') ||
    (!queryUtil.isParameterTrue('suppressbi') &&
      !queryUtil.isParameterTrue('isqa') &&
      !queryUtil.getParameterByName('EditorSource') &&
      !queryUtil.getParameterByName('santa-editor-override'));

  // @ts-expect-error
  const releaseVersion = __BUNDLED_EDITOR_VERSION__;
  const release = releaseVersion ? `santa-editor@${releaseVersion}` : undefined;

  const dsn =
    'https://f432f47cfde34ebc9d320d006a71c57e@sentry-next.wixpress.com/7163';

  Sentry.init({
    dsn,
    environment: 'production',
    release,
    transport: CustomTransport,
    autoSessionTracking: true,
    enabled,
    // https://stackoverflow.com/a/50387233/223750
    // https://sentry-next.wixpress.com/organizations/santa-editor/issues/21294/
    ignoreErrors: ['ResizeObserver loop limit exceeded'],
  });

  Sentry.configureScope((scope: SentryTypes.Scope) => {
    scope.setTags(
      buildDefaultTagStore(
        editorModel,
        editorVersion,
        loggerModel,
        appStudioModel,
      ),
    );
    scope.setExtras(buildDefaultContext(editorModel));
    scope.setUser({
      id: editorModel.permissionsInfo?.loggedInUserId,
      roles: editorModel.permissionsInfo?.loggedInUserRoles,
    });

    scope.addEventProcessor((event, hint) => {
      if (
        shouldSkipEvent(
          typeof hint?.originalException === 'string'
            ? hint.originalException
            : hint?.originalException?.message,
        )
      ) {
        return null;
      }

      event.extra = {
        ...event.extra,
        biHistoryUrl:
          `https://bo.wix.com/bi-ux/#/history?timezone=0` +
          `&fields=date_created,src,evid,evid_desc,error_type,error_info,tags,extra,error_context,origin,uuid,msid,esi,dm_version,os_name,browser_family,browser_name,app_name,country_code` +
          `&uuid=${editorModel.permissionsInfo.ownerId}` +
          `&start=${String(Date.now() - 4 /*minutes*/ * 60000)}` +
          `&stop=${String(Date.now() + 4 /*minutes*/ * 60000)}` +
          `&wql=msid%3D%22${editorModel.metaSiteId}%22` +
          `&order=asc` +
          `&levels=1,2,3,4`,
      };

      event.tags = event.tags || {};

      if (isWixCodeError(event)) {
        event.tags = {
          ...event.tags,
          'wix-code-error': true,
        };

        event.fingerprint = (event.fingerprint || []).concat([
          'wix-code-error',
        ]);
      }

      if (event.tags.hasOwnProperty('whiteScreenOfDeath')) {
        // https://docs.sentry.io/platforms/node/data-management/event-grouping/sdk-fingerprinting/
        event.fingerprint = ['{{ default }}', 'whiteScreenOfDeath'];
      }

      return event;
    });
  });
};

initSentry();
